import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, Plug, Upload, Loader2, ArrowUpRight, XCircle, RotateCcw, AlertCircle, Lock, ChevronUp, MapPin, Trash2 } from "lucide-react";
import { api, num } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Badge, Loading } from "@/components/kit";

const ORDER = ["starter", "pro", "multi"];

// Static provider catalog — connection state comes from /integrations
const PROVIDERS = [
  { key: "lightspeed", name: "Lightspeed Retail", configured: true },
  { key: "square", name: "Square", configured: true },
  { key: "vend", name: "Vend (Lightspeed)", configured: false },
  { key: "shopify", name: "Shopify POS", configured: false },
  { key: "toast", name: "Toast", configured: false },
];

export default function Settings() {
  const { user, activeStore, stores, subscription, setSubscription, hasFeature, getMaxStores } = useAuth();
  const [sub, setSub] = useState(null);
  const [pos, setPos] = useState([]);
  const [realIntegrations, setRealIntegrations] = useState([]);
  const [subscribing, setSubscribing] = useState(null);
  const [importing, setImporting] = useState(false);
  const [summary, setSummary] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [renewing, setRenewing] = useState(false);
  const [city, setCity] = useState("");
  const [savingCity, setSavingCity] = useState(false);
  const [resetting, setResetting] = useState(false);
  const fileRef = useRef();

  // Load current store city once the active store is known
  useEffect(() => {
    if (!activeStore || !stores.length) return;
    const st = stores.find((s) => s.id === activeStore);
    if (st) setCity(st.city || "");
  }, [activeStore, stores]);

  const saveCity = async () => {
    setSavingCity(true);
    try {
      await api.put(`/stores/${activeStore}`, { city: city.trim() });
      toast.success(`Store location set to ${city.trim()} — weather now uses this city`);
      window.location.reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update city");
    } finally {
      setSavingCity(false);
    }
  };

  const resetStoreData = async () => {
    if (!window.confirm("Delete ALL data for this store — products, sales history, suppliers, weather? This cannot be undone. Use this to remove sample/dummy data before importing your real sales.")) return;
    setResetting(true);
    try {
      await api.post(`/stores/${activeStore}/reset-data`);
      toast.success("Store data cleared");
      window.location.reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not clear store data");
    } finally {
      setResetting(false);
    }
  };

  // Sync subscription state from AuthContext
  useEffect(() => {
    if (subscription) setSub(subscription);
  }, [subscription]);

  const loadIntegrations = useCallback(() => {
    if (!hasFeature("pos_integrations")) {
      setPos([]);
      setRealIntegrations([]);
      return;
    }
    api.get("/integrations").then(({ data }) => {
      if (data.integrations && Array.isArray(data.integrations)) {
        setRealIntegrations(data.integrations);
        setPos(data.integrations);
      }
    }).catch(() => {});
  }, [hasFeature]);

  useEffect(() => {
    loadIntegrations();
  }, [subscription, hasFeature, loadIntegrations]);

  const subscribe = async (lookup_key, key) => {
    setSubscribing(key);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setSubscribing(null);
    }
  };

  const cancelSubscription = async () => {
    setCancelling(true);
    try {
      await api.post("/subscription/cancel");
      toast.success("Subscription cancelled. You'll keep access until the trial ends or your current billing period finishes.");
      if (setSubscription) setSubscription(null);
      window.location.reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not cancel subscription");
    } finally {
      setCancelling(false);
    }
  };

  const renewSubscription = async () => {
    setRenewing(true);
    try {
      const { data } = await api.post("/subscription/renew");
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start renewal");
    } finally {
      setRenewing(false);
    }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImporting(true);
    setSummary(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(`/sales/import?store_id=${activeStore}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSummary(data);
      toast.success("Sales data imported — forecasts updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const connectPOS = async (provider) => {
    try {
      const { data } = await api.get(`/integrations/connect/${provider}?store_id=${activeStore}`);
      window.location.href = data.authorize_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to connect POS");
    }
  };

  const disconnectPOS = async (integrationId) => {
    try {
      await api.post(`/integrations/disconnect/${integrationId}`);
      toast.success("POS disconnected");
      loadIntegrations();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to disconnect");
    }
  };

  const syncPOS = async (integrationId) => {
    try {
      toast.info("Syncing transactions...");
      const { data } = await api.post(`/integrations/sync/${integrationId}`);
      if (data.success) {
        toast.success(`Imported ${data.result.transactions_imported} transactions`);
        loadIntegrations();
      } else {
        toast.error(data.result.error_message || "Sync failed");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    }
  };

  const downloadTemplate = () => {
    const csvContent = "date,time,product,category,quantity,revenue,store\n2026-08-01,08:15,Flat White,Coffee,2,11.00,main\n2026-08-01,08:30,Croissant,Food,1,6.00,main\n2026-08-01,09:00,Iced Latte,Cold Drinks,1,6.50,main\n2026-08-02,08:20,Latte,Coffee,3,16.50,main\n";
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "optics_sales_template.csv";
    a.click();
  };

  if (!sub) return <Loading />;

  return (
    <div>
      <PageHeader title="Settings" subtitle={`Signed in as ${user?.email} · role ${user?.role}`} />

      {/* Subscription */}
      <div className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-400 mb-3">Subscription · current: {sub.current}</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
        {ORDER.map((key) => {
          const t = sub.tiers[key];
          const current = sub.current === key;
          return (
            <Card key={key} className={`p-6 flex flex-col ${current ? "border-zinc-900 ring-1 ring-zinc-900" : ""}`} testid={`tier-${key}`}>
              <div className="flex items-center justify-between">
                <div className="font-display font-medium">{t.name}</div>
                {current && <Badge level="success">Current</Badge>}
              </div>
              <div className="mt-2 font-display text-3xl font-semibold tnum">NZ${t.price}<span className="text-sm text-zinc-400 font-normal">/mo</span></div>
              <ul className="mt-4 space-y-2 text-sm text-zinc-600 flex-1">
                {t.features.map((ff) => <li key={ff} className="flex items-center gap-2"><Check className="h-3.5 w-3.5 text-emerald-600" /> {ff}</li>)}
              </ul>
              <button
                data-testid={`subscribe-${key}`}
                disabled={current || subscribing}
                onClick={() => subscribe(t.lookup_key, key)}
                className={`mt-6 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 ${current ? "bg-zinc-100 text-zinc-400 cursor-default" : "bg-zinc-900 text-white hover:bg-zinc-800"}`}
              >
                {subscribing === key ? <Loader2 className="h-4 w-4 animate-spin" /> : current ? "Current plan" : <>Subscribe <ArrowUpRight className="h-4 w-4" /></>}
              </button>
            </Card>
          );
        })}
      </div>
      <p className="text-xs text-zinc-400 mb-8">Secure checkout by Stripe in NZD. Cancel anytime from this page — access continues until the end of your billing period.</p>

      {/* Plan feature matrix — what's included / locked */}
      <div className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-400 mb-3">Plan features</div>
      <Card className="p-4 mb-8" testid="plan-features">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {[
            { key: "forecast", label: "Forecasting" },
            { key: "purchasing", label: "Purchasing" },
            { key: "insights", label: "AI Insights" },
            { key: "accuracy", label: "Forecast Accuracy" },
            { key: "weather", label: "Weather Intel" },
            { key: "inventory", label: "Inventory" },
            { key: "events", label: "Events & Holidays" },
            { key: "waste", label: "Waste Analytics" },
            { key: "pos_integrations", label: "POS Integrations" },
            { key: "multi_store", label: "Multi-location" },
          ].map((f) => (
            <div key={f.key} className={`flex items-center gap-2 p-2 rounded-lg ${hasFeature(f.key) ? "bg-emerald-50 text-emerald-700" : "bg-zinc-50 text-zinc-400"}`}>
              {hasFeature(f.key) ? <Check className="h-4 w-4 shrink-0" /> : <Lock className="h-4 w-4 shrink-0" />}
              <span className="text-sm font-medium">{f.label}</span>
              {!hasFeature(f.key) && sub.current !== "multi" && <span className="ml-auto text-[10px] text-amber-600">Pro+</span>}
            </div>
          ))}
        </div>
        {sub.current !== "multi" && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
            <div className="font-medium flex items-center gap-1.5"><ChevronUp className="h-3.5 w-3.5" /> Upgrade to Pro or Multi-site for full access</div>
            <div className="text-xs mt-1">Current plan: <span className="font-semibold capitalize">{sub.current}</span></div>
          </div>
        )}
      </Card>

      {/* Cancel / Renew actions */}
      {sub.current !== "none" && (
        <Card className="p-4 mb-8 bg-zinc-50 border-zinc-200" testid="subscription-actions">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3 text-sm">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <span className="text-zinc-700">
                {sub.trial_days_left != null
                  ? `Trial ends in ${sub.trial_days_left} day${sub.trial_days_left !== 1 ? "s" : ""}. Card not charged until then.`
                  : "Active subscription — cancels at end of current billing period."}
              </span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={cancelSubscription}
                disabled={cancelling}
                className="border border-red-300 text-red-600 px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-50 transition-colors duration-200 disabled:opacity-50"
                data-testid="cancel-subscription"
              >
                {cancelling ? <Loader2 className="h-4 w-4 animate-spin" /> : <> <XCircle className="h-4 w-4 inline mr-1" /> Cancel subscription</>}
              </button>
              {sub.stripe_subscription_id && sub.current !== "multi" && (
                <button
                  onClick={renewSubscription}
                  disabled={renewing}
                  className="border border-zinc-200 text-zinc-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-zinc-100 transition-colors duration-200 disabled:opacity-50"
                  data-testid="renew-subscription"
                >
                  {renewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <> <RotateCcw className="h-4 w-4 inline mr-1" /> Change plan</>}
                </button>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* Store location & data */}
      <div className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-400 mb-3">Store location & data</div>
      <Card className="p-5 mb-8" testid="store-settings">
        <div className="flex flex-wrap items-end gap-4">
          <label className="block flex-1 min-w-[220px]">
            <span className="text-xs font-semibold uppercase tracking-wider text-zinc-500 flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5" /> City (used for live weather forecasts)
            </span>
            <div className="mt-1.5 flex gap-2">
              <input value={city} onChange={(e) => setCity(e.target.value)} placeholder="e.g. Wellington"
                className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900" />
              <button onClick={saveCity} disabled={savingCity || !city.trim()}
                className="bg-zinc-900 hover:bg-zinc-800 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors">
                {savingCity ? "Saving…" : "Save city"}
              </button>
            </div>
          </label>
          <button onClick={resetStoreData} disabled={resetting}
            className="inline-flex items-center gap-1.5 border border-red-200 text-red-600 hover:bg-red-50 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 disabled:opacity-50"
            data-testid="reset-store-data">
            <Trash2 className="h-3.5 w-3.5" /> {resetting ? "Clearing…" : "Clear all store data"}
          </button>
        </div>
        <p className="mt-3 text-xs text-zinc-400">
          Saving a new city re-fetches weather for that location and removes old demo weather rows. "Clear all store data" deletes everything for this store so you can start clean.
        </p>
      </Card>

      {/* CSV upload */}
      <div className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-400 mb-3">Sales data import</div>
      <Card className="p-8 mb-3 border-dashed text-center" testid="csv-upload">
        {importing ? (
          <><Loader2 className="h-6 w-6 mx-auto text-zinc-400 animate-spin" /><div className="mt-3 text-sm text-zinc-500">Parsing & aggregating your sales…</div></>
        ) : (
          <>
            <Upload className="h-6 w-6 mx-auto text-zinc-400" />
            <div className="mt-3 text-sm text-zinc-600">Upload historical sales CSV or Excel — columns: <code className="text-xs bg-zinc-100 px-1.5 py-0.5 rounded">date, time, product, category, quantity, revenue, store</code></div>
            <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" onChange={onFile} data-testid="csv-input" className="hidden" id="csv-file" />
            <div className="mt-4 flex items-center justify-center gap-3">
              <label htmlFor="csv-file" className="inline-block cursor-pointer bg-zinc-900 hover:bg-zinc-800 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200">Choose CSV or Excel file</label>
              <button type="button" onClick={downloadTemplate} className="border border-zinc-300 hover:bg-zinc-50 text-zinc-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200">Download CSV Template</button>
            </div>
          </>
        )}
      </Card>
      {summary && (
        <Card className="p-5 mb-8 bg-emerald-50/40 border-emerald-200" testid="import-summary">
          <div className="text-sm text-emerald-800 space-y-1">
            <div className="font-semibold">Import summary</div>
            <div>{num(summary.transactions_imported)} transactions imported</div>
            <div>{num(summary.products_detected)} products detected</div>
            <div>{num(summary.months_processed)} months of historical data processed</div>
            <div>{num(summary.daily_records_created)} daily records fed into the forecasting engine{summary.missing_values_skipped ? ` · ${summary.missing_values_skipped} invalid rows skipped` : ""}</div>
            {summary.weather_days_backfilled > 0 && (
              <div className="text-emerald-700 font-medium">☀ {num(summary.weather_days_backfilled)} days of real historical weather matched to your sales — Weather Intelligence is now live</div>
            )}
          </div>
        </Card>
      )}

      {/* POS integrations */}
      <div className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-400 mb-3">POS integrations</div>
      <Card className="overflow-hidden mb-3" testid="integrations">
        {PROVIDERS.map((prov) => {
          const key = prov.key;
          const realInt = realIntegrations.find(r => r.provider === key);
          const isConnected = realInt?.status === "connected";
          const hasConfig = prov.configured; // Only these are implemented

          return (
            <div key={key} className="flex items-center justify-between px-5 py-4 border-b border-zinc-50 last:border-0">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-lg bg-zinc-100 grid place-items-center"><Plug className="h-4 w-4 text-zinc-500" /></div>
                <div>
                  <div className="font-medium text-zinc-900">
                    {prov.name}
                    {!hasConfig && <span className="ml-2 text-xs text-zinc-400">(Coming soon)</span>}
                  </div>
                  <div className="text-xs text-zinc-400">
                    {isConnected
                      ? `${realInt.external_store_name || "Connected"} · ${num(realInt.total_transactions_imported)} txns imported`
                      : "Not connected"}
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                {isConnected ? (
                  <>
                    <button onClick={() => syncPOS(realInt.id)}
                      className="border border-zinc-200 px-4 py-2 rounded-lg text-sm font-medium hover:bg-zinc-50 transition-colors duration-200">
                      Sync Now
                    </button>
                    <button onClick={() => disconnectPOS(realInt.id)}
                      className="border border-red-200 text-red-600 px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-50 transition-colors duration-200">
                      Disconnect
                    </button>
                  </>
                ) : (
                  <button
                    data-testid={`connect-${key}`}
                    disabled={!hasConfig}
                    onClick={() => hasConfig ? connectPOS(key) : toast.info(`${prov.name} integration coming soon`)}
                    className={`border border-zinc-200 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                      hasConfig ? "hover:bg-zinc-50" : "opacity-50 cursor-not-allowed"
                    }`}>
                    Connect
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </Card>
      <p className="text-xs text-zinc-400">
        {realIntegrations.length > 0
          ? "POS integrations sync automatically every 15 minutes. Use 'Sync Now' for immediate sync."
          : "Connect your POS system to automatically import sales transactions. Lightspeed and Square are available now."}
      </p>
      {!hasFeature("pos_integrations") && (
        <Card className="p-6 mb-3 bg-zinc-50 border-zinc-200 text-center" testid="pos-upgrade">
          <Lock className="h-8 w-8 mx-auto text-zinc-300" />
          <div className="mt-2 font-medium text-zinc-700">POS Integrations</div>
          <div className="mt-1 text-sm text-zinc-500">Available on Pro and Multi-site plans</div>
          <Link to="/app/settings" className="mt-4 inline-block text-amber-700 font-semibold hover:underline">View plans to upgrade</Link>
        </Card>
      )}
    </div>
  );
}
